import { Fragment } from 'react'

import { Card, Pill } from '../ui'
import type { ComparisonFeatureDelta, ComparisonResult, ComparisonScoreDelta } from '../../lib/api'
import { comparisonNumber } from '../../lib/format'
import type { ComparisonScoreLabels } from './ComparisonLabels'

/** Short variation copy is fixed on Compare/PhoneCompare; all explanations are payload text. */
const VARIATION = {
  beyond: { long: 'Beyond it', short: 'Beyond', tone: 'cyan' },
  within: { long: 'Within it', short: 'Within', tone: 'neutral' },
  not_yet_known: { long: 'Not yet known', short: 'Not yet known', tone: 'neutral' },
} as const

function Variation({ row, separate = false }: { row: ComparisonFeatureDelta; separate?: boolean }) {
  if (row.scale === 'quality') return <Pill className="comparison__variation comparison__variation--quality" title={row.text}>
    <span className="comparison__desktop">Quality-scaled</span><span className="comparison__phone">Quality</span>
  </Pill>
  if (row.variation == null) return <span className="comparison__muted">{separate ? 'not available' : row.reason ?? 'not available'}</span>
  const label = VARIATION[row.variation.status]
  return <Pill tone={label.tone} className="comparison__variation" title={row.variation.text}>
    <span className="comparison__desktop">{label.long}</span><span className="comparison__phone">{label.short}</span>
  </Pill>
}

function Delta({ row, scores, separate = false }: { row: ComparisonFeatureDelta; scores: boolean; separate?: boolean }) {
  const original = !scores || row.score_delta === null
  const value = original ? row.delta : row.score_delta
  const printed = comparisonNumber(value, true)
  // Direction is printed as well as coloured. No magnitude threshold or benefit judgement.
  const direction = printed.startsWith('+') ? 'up' : printed.startsWith('-') ? 'down' : 'flat'
  return <span className="comparison__change">
    <span className={`comparison__delta comparison__delta--${direction}${separate && value === null ? ' comparison__missing' : ''}`} data-testid="comparison-delta">
      {printed}{original && value !== null && row.unit !== null ? ` ${row.unit}` : ''}
    </span>
  </span>
}

export function ComparisonRows({ result, beforeTime, afterTime, scoreLabels = {} }: {
  result: ComparisonResult; beforeTime: string; afterTime: string
  scoreLabels?: ComparisonScoreLabels
}) {
  // The implemented API separates composite scores from their raw ingredients.
  // Older board examples omit that collection and retain their original layout.
  const separate = result.score_deltas !== undefined
  const scores = !separate && result.baseline_status === 'available'
  const features = <Card as="section" className="comparison__measures" aria-label={separate ? 'Original-unit features' : 'What differed'}>
    {separate && <h2 className="comparison__label">Original-unit features</h2>}
    <table className="comparison__table" data-testid="comparison-table">
      <caption className="visually-hidden">{separate ? 'Original-unit features' : 'What differed between the readings'}</caption>
      <thead><tr>
        <th scope="col">Measure</th><th scope="col" className={scores ? undefined : 'comparison__original-side'}>{beforeTime}{scores && <span className="visually-hidden"> score</span>}</th>
        <th scope="col" className={scores ? undefined : 'comparison__original-side'}>{afterTime}{scores && <span className="visually-hidden"> score</span>}</th>
        <th scope="col"><span className="comparison__desktop">Change</span><span className="comparison__phone">Δ</span></th>
        <th scope="col"><span className="comparison__desktop">Against your repeat variation</span><span className="comparison__phone">vs repeat</span></th>
      </tr></thead>
      <tbody>{result.feature_deltas.map((row) => <tr key={row.feature_key} data-feature={row.feature_key}>
        <th scope="row" title={row.title}>{row.user_title ?? row.title}
          {!separate && row.score_delta_reason != null && row.score_delta_reason !== 'no_baseline' && <Pill className="comparison__reason" title={row.text}>{row.score_delta_reason}</Pill>}
          {separate && [...new Set([...(row.reasons ?? []), ...(row.reason === null ? [] : [row.reason])])].map((reason) => <Pill key={reason} className="comparison__reason" title={row.text}>{reason}</Pill>)}
        </th>
        <td className={scores ? 'comparison__number' : 'comparison__number comparison__original-side'}>{separate && row.before === null ? <span className="comparison__missing">{comparisonNumber(row.before)}</span> : comparisonNumber(scores ? row.score_before : row.before)}{!scores && row.before !== null && row.unit !== null ? ` ${row.unit}` : ''}</td>
        <td className={scores ? 'comparison__number' : 'comparison__number comparison__original-side'}>{separate && row.after === null ? <span className="comparison__missing">{comparisonNumber(row.after)}</span> : comparisonNumber(scores ? row.score_after : row.after)}{!scores && row.after !== null && row.unit !== null ? ` ${row.unit}` : ''}</td>
        <td><Delta row={row} scores={scores} separate={separate} /></td>
        <td><Variation row={row} separate={separate} /></td>
      </tr>)}</tbody>
    </table>
    {result.baseline_text.trim() && <p className="comparison__note">{result.baseline_text}</p>}
    {result.variation.text.trim() && <p className="comparison__note">{result.variation.text}</p>}
  </Card>
  if (!separate) return features
  const hasValues = Object.values(result.score_deltas!).some((row) => row.before !== null || row.after !== null || row.delta !== null)
  if (!hasValues) return <div className="comparison__measure-groups">
    {features}
    {Boolean(Object.keys(result.score_deltas!).length) && <details className="comparison__raw-details">
      <summary>Score details</summary><CompositeScores rows={result.score_deltas!} beforeTime={beforeTime} afterTime={afterTime} labels={scoreLabels} />
    </details>}
  </div>
  return <div className="comparison__measure-groups">
    <CompositeScores rows={result.score_deltas!} beforeTime={beforeTime} afterTime={afterTime} labels={scoreLabels} />
    <details className="comparison__raw-details"><summary>Measurement details</summary>{features}</details>
  </div>
}
/** Compared-recording metadata names only exact matching keys; no ingredient linkage. */
function CompositeScores({ rows, beforeTime, afterTime, labels }: {
  rows: Record<string, ComparisonScoreDelta>; beforeTime: string; afterTime: string
  labels: ComparisonScoreLabels
}) {
  const hasValues = Object.values(rows).some((row) => row.before !== null || row.after !== null || row.delta !== null)
  return <Card as="section" className="comparison__measures" aria-label="Composite scores">
    <h2 className="comparison__label">What differed</h2>
    {hasValues ? <table className="comparison__table comparison__score-table" data-testid="comparison-score-table">
      <caption className="visually-hidden">Composite scores</caption>
      <thead><tr><th scope="col">Measure</th><th scope="col">{beforeTime}<span className="visually-hidden"> score</span></th>
        <th scope="col">{afterTime}<span className="visually-hidden"> score</span></th><th scope="col">Change</th></tr></thead>
      <tbody>{Object.entries(rows).map(([key, row]) => {
        const printed = comparisonNumber(row.delta, true)
        const direction = printed.startsWith('+') ? 'up' : printed.startsWith('-') ? 'down' : 'flat'
        return <tr key={key} data-score={key}>
          <th scope="row" title={labels[key]?.technical ?? key}>{labels[key]?.title ?? key}
            {row.reason !== null && <Pill className="comparison__reason" title={row.reason}>{row.reason.replaceAll('_', ' ')}</Pill>}
            {row.scale === 'quality' && <Pill className="comparison__variation comparison__variation--quality">Quality-scaled</Pill>}
          </th>
          <td className="comparison__number">{row.before === null ? <span className="comparison__missing">{comparisonNumber(row.before)}</span> : comparisonNumber(row.before)}</td><td className="comparison__number">{row.after === null ? <span className="comparison__missing">{comparisonNumber(row.after)}</span> : comparisonNumber(row.after)}</td>
          <td><span className={`comparison__delta comparison__delta--${direction}${row.delta === null ? ' comparison__missing' : ''}`} data-testid="comparison-score-delta">{printed}{row.delta !== null && row.unit ? ` ${row.unit}` : ''}</span></td>
        </tr>
      })}</tbody>
    </table> : <dl className="comparison__kv">{Object.entries(rows).map(([key, row]) => <Fragment key={key}>
      <dt title={labels[key]?.technical ?? key}>{labels[key]?.title ?? key}</dt><dd>{row.reason?.replaceAll('_', ' ') ?? 'not available'}</dd>
    </Fragment>)}</dl>}
  </Card>
}
